import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { useFieldControl } from '@/components/ui/field-context';
import { cn } from '@/lib/utils';

/**
 * Champ de saisie (components/Field) : contour `input` (plus foncé que
 * `border`), hauteur 32px (28px en `controlSize="sm"` dans une barre de
 * tableau), contour `danger-fg` si `aria-invalid`. `numeric` = `input-num` :
 * police mono, chiffres tabulaires, aligné à droite.
 */
const inputVariants = cva(
  [
    'flex w-full min-w-0 rounded-md border border-input bg-background px-3 text-sm text-foreground',
    'placeholder:text-muted-foreground',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    'disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground',
    'aria-[invalid=true]:border-danger-fg',
    'file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground',
  ].join(' '),
  {
    variants: {
      controlSize: {
        default: 'h-8',
        sm: 'h-7 text-[13px]',
      },
      numeric: {
        true: 'text-right font-mono tabular-nums',
        false: '',
      },
    },
    defaultVariants: {
      controlSize: 'default',
      numeric: false,
    },
  },
);

export interface InputProps
  extends React.ComponentProps<'input'>, VariantProps<typeof inputVariants> {}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, controlSize, numeric, ...props }, ref) => {
    const a11y = useFieldControl(props);
    return (
      <input
        type={type}
        className={cn(inputVariants({ controlSize, numeric }), className)}
        ref={ref}
        {...props}
        {...a11y}
      />
    );
  },
);
Input.displayName = 'Input';

export { Input, inputVariants };
